import { useDatabase } from "@nozbe/watermelondb/hooks";
import { StackScreenProps } from "@react-navigation/stack";
import {
  ModalCloseButton,
  Page,
  TextField,
} from "@towersystems/roam-common-native";
import { useEffect, useMemo, useState } from "react";
import { CloseShiftRootScreenParamList } from "../../../../types";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import {
  moneyToFormattedString,
  valueAsMoney,
} from "../../../../utilities/money";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { useCloseShiftProcessProvider } from "../../utils/shift/hooks";
import { useCollectCashForm as useCountCashForm } from "../../utils/use-collect-cash-form";

export interface CloseCountCashScreenProps
  extends StackScreenProps<
    CloseShiftRootScreenParamList,
    "CloseCountCashScreen"
  > {}

export const CloseCountCashScreen = ({
  navigation,
  route,
}: CloseCountCashScreenProps) => {
  const globalSettings = useGlobalSettingsProvider();
  const { getNextPaymentMethod, countPaymentMethod, payments } =
    useCloseShiftProcessProvider();

  const paymentMethodId =
    route.params?.paymentMethodId ?? getNextPaymentMethod()?.paymentMethodId;

  const countablePaymentMethod = useMemo(() => {
    return payments.find((p) => p.paymentMethodId === paymentMethodId);
  }, [paymentMethodId, payments]);

  const defaultValue = useMemo(() => {
    return payments.find(
      (p) => p.paymentMethodId === countablePaymentMethod?.paymentMethodId
    )?.counted;
  }, [payments, countablePaymentMethod?.paymentMethodId]);

  const { amount, isValid, setAmount } = useCountCashForm({
    defaultValues: { amount: valueAsMoney(defaultValue?.toString() || "0.00") },
  });

  const database = useDatabase();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(
    undefined
  );

  const shouldFocus = useHasTransitionEnded(navigation);

  useEffect(() => {
    countablePaymentMethod?.paymentMethodId &&
      database
        .get<PaymentMethod>("payment_methods")
        .find(countablePaymentMethod.paymentMethodId)
        .then(setPaymentMethod)
        .catch((err) => console.log(err));
  }, [countablePaymentMethod?.paymentMethodId]);

  const handleOnSubmit = () => {
    if (countablePaymentMethod && isValid) {
      const nextPaymentMethod = countPaymentMethod(
        countablePaymentMethod.paymentMethodId,
        parseFloat(amount)
      );
      if (nextPaymentMethod) {
        console.log("ROUTING TO: ", nextPaymentMethod.paymentMethodId)
        navigation.push("CloseCountCashScreen", {
          paymentMethodId: nextPaymentMethod.paymentMethodId,
        });
      } else {
        console.log("Navigating to close shift screen")
        navigation.navigate("CloseShiftNoteScreen");
      }
    }
  };

  useEffect(() => {
    const disabled = !paymentMethod?.id || !isValid;
    // loop through all payments untill all have been counted
    navigation.setOptions({
      headerRight: () => (
        <ModalCloseButton
          disabled={disabled}
          onPress={handleOnSubmit}
          type="Continue"
        />
      ),
    });
  }, [paymentMethod?.id, isValid, handleOnSubmit]);

  if (!paymentMethod) {
    return null;
  }

  const title = globalSettings.showExpectedClosureCounts
    ? `Expected ${
        moneyToFormattedString(countablePaymentMethod?.expected) || 0
      }`
    : paymentMethod.name;

  const subtitle = globalSettings.showExpectedClosureCounts
    ? paymentMethod.name
    : "Count Cash in drawer";

  return (
    <Page
      title={title}
      secondaryTitle={subtitle}
      titleContainerStyle={{ alignItems: "center" }}
    >
      <TextField
        placeholder="Amount Counted"
        label="Amount Counted"
        value={amount}
        onChangeText={setAmount}
        autoFocus={shouldFocus}
        keyboardType="numeric"
        formatterType={isValid ? "money" : undefined}
        validationMessage={!isValid ? "Amount is invalid" : undefined}
      />
    </Page>
  );
};
